import { useQuery } from '@apollo/client'
import { useNavigate } from 'react-router-dom'
import { GET_ESTADISTICAS, GET_ALERTAS_VENCIMIENTO, GET_SOLICITUDES_PENDIENTES } from '../graphql/queries'
import './Dashboard.css'

function Dashboard() {
  const navigate = useNavigate()

  const { data: statsData, loading: statsLoading } = useQuery(GET_ESTADISTICAS)
  const { data: alertasData, loading: alertasLoading } = useQuery(GET_ALERTAS_VENCIMIENTO, {
    variables: { dias: 30 }
  })
  const { data: solicitudesData, loading: solicitudesLoading } = useQuery(GET_SOLICITUDES_PENDIENTES)

  const stats = statsData?.estadisticas || {}
  const alertas = alertasData?.alertasVencimiento || []
  const solicitudes = solicitudesData?.solicitudesPendientes || []

  const getNivelAlertaClass = (nivel) => {
    switch (nivel) {
      case 'critico': return 'alerta-critico'
      case 'urgente': return 'alerta-urgente'
      default: return 'alerta-proximo'
    }
  }

  const getNivelAlertaIcon = (nivel) => {
    switch (nivel) {
      case 'critico': return '🔴'
      case 'urgente': return '🟠'
      default: return '🟡'
    }
  }

  if (statsLoading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    )
  }

  return (
    <div className="dashboard">
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <p className="page-subtitle">Resumen general del sistema de licencias</p>
      </div>

      {/* Estadísticas */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon stat-icon-primary">🏢</div>
          <div className="stat-content">
            <span className="stat-value">{stats.comercios_activos || 0}</span>
            <span className="stat-label">Comercios Activos</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon stat-icon-success">✓</div>
          <div className="stat-content">
            <span className="stat-value">{stats.licencias_activas || 0}</span>
            <span className="stat-label">Licencias Activas</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon stat-icon-warning">⚠</div>
          <div className="stat-content">
            <span className="stat-value">{stats.licencias_por_vencer || 0}</span>
            <span className="stat-label">Por Vencer (30 días)</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon stat-icon-info">📝</div>
          <div className="stat-content">
            <span className="stat-value">{stats.solicitudes_pendientes || 0}</span>
            <span className="stat-label">Solicitudes Pendientes</span>
          </div>
        </div>
      </div>

      <div className="dashboard-grid">
        {/* Alertas de Vencimiento */}
        <div className="card">
          <div className="card-header flex-between">
            <h3>Alertas de Vencimiento</h3>
            <span className="text-muted">Próximos 30 días</span>
          </div>
          <div className="card-body">
            {alertasLoading ? (
              <div className="loading"><div className="spinner"></div></div>
            ) : alertas.length === 0 ? (
              <p className="text-center text-muted">No hay licencias próximas a vencer</p>
            ) : (
              <div className="alertas-list">
                {alertas.slice(0, 5).map((alerta) => (
                  <div
                    key={alerta.licencia_id}
                    className={`alerta-item ${getNivelAlertaClass(alerta.nivel_alerta)}`}
                    onClick={() => navigate(`/comercios/${alerta.comercio_id}`)}
                  >
                    <span className="alerta-icon">{getNivelAlertaIcon(alerta.nivel_alerta)}</span>
                    <div className="alerta-content">
                      <span className="alerta-comercio">{alerta.nombre_comercial}</span>
                      <span className="alerta-detalle">
                        Vence en {alerta.dias_restantes} días
                      </span>
                    </div>
                    <button className="btn btn-sm btn-primary">Renovar</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Solicitudes Pendientes */}
        <div className="card">
          <div className="card-header flex-between">
            <h3>Solicitudes Pendientes</h3>
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => navigate('/solicitudes')}
            >
              Ver todas
            </button>
          </div>
          <div className="card-body">
            {solicitudesLoading ? (
              <div className="loading"><div className="spinner"></div></div>
            ) : solicitudes.length === 0 ? (
              <p className="text-center text-muted">No hay solicitudes pendientes</p>
            ) : (
              <div className="solicitudes-list">
                {solicitudes.slice(0, 5).map((solicitud) => (
                  <div key={solicitud.id} className="solicitud-item">
                    <div className="solicitud-content">
                      <span className="solicitud-comercio">{solicitud.nombre_comercial}</span>
                      <span className="solicitud-detalle">
                        {solicitud.tipo_licencia_nombre} • {solicitud.tipo_interfaz}
                      </span>
                    </div>
                    <div className="solicitud-actions">
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => navigate('/solicitudes')}
                      >
                        Aprobar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Resumen adicional */}
      <div className="card mt-3">
        <div className="card-header">
          <h3>Resumen del Sistema</h3>
        </div>
        <div className="card-body">
          <div className="resumen-grid">
            <div className="resumen-item">
              <span className="resumen-label">Total Comercios</span>
              <span className="resumen-value">{stats.total_comercios || 0}</span>
            </div>
            <div className="resumen-item">
              <span className="resumen-label">Comercios Pendientes</span>
              <span className="resumen-value">{stats.comercios_pendientes || 0}</span>
            </div>
            <div className="resumen-item">
              <span className="resumen-label">Comercios Suspendidos</span>
              <span className="resumen-value">{stats.comercios_suspendidos || 0}</span>
            </div>
            <div className="resumen-item">
              <span className="resumen-label">Licencias Vencidas</span>
              <span className="resumen-value">{stats.licencias_vencidas || 0}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
